import { Box, IconButton } from '@mui/material';
import { useAuth } from '@schooly/components/authentication';
import { DropdownComments, NoteFilledIcon, NotePlusIcon } from '@schooly/style';
import { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import { JustificationSave } from '@schooly/api';
import { TakeAttendanceEntry, useTakeAttendanceModal } from './TakeAttendanceModalContext';

interface TakeAttendanceGridCommentCellProps {
  entry: TakeAttendanceEntry;
  justificationNote: JustificationSave | null;
  editMode?: boolean;
}

export const TAKE_ATTENDANCE_COMMENT_CELL_CLASS = 'TakeAttendanceCommentCell';

export const TakeAttendanceCommentCell: FC<TakeAttendanceGridCommentCellProps> = ({
  entry,
  justificationNote,
  editMode,
}) => {
  const { currentStaff } = useAuth();
  const { formatMessage } = useIntl();
  const [isOpened, setOpened] = useState(false);
  const { isSaving, onUpdateAttendanceEntry } = useTakeAttendanceModal();
  const hasComment = !!entry?.text;

  const handleCommentChange = (text: string) => {
    onUpdateAttendanceEntry({ ...entry, text }, justificationNote);
  };

  return (
    <Box
      className={TAKE_ATTENDANCE_COMMENT_CELL_CLASS}
      sx={(theme) => ({
        position: 'relative',
        minWidth: 40,
        height: theme.spacing(4),
        right: '-1px',
        opacity: isOpened || hasComment ? `1 !important` : undefined,
        color: isOpened ? theme.palette.primary.main : theme.palette.common.grey,
        [theme.breakpoints.down('lg')]: {
          minWidth: 100,
        },
      })}
    >
      <DropdownComments
        onToggle={setOpened}
        autoFocusOnEdit={editMode}
        comments={
          entry?.text
            ? [
                {
                  comment: entry.text,
                  relation_id: entry.creator_relation_id ?? '',
                  title: entry.creator_title,
                  given_name: entry.creator_given_name,
                  last_name: entry.creator_last_name ?? '',
                  known_as: entry.creator_known_as,
                },
              ]
            : []
        }
        canAdd={!entry?.text} // if no comment yet
        onAdd={editMode ? handleCommentChange : undefined}
        onEdit={editMode ? handleCommentChange : undefined}
        disabled={isSaving}
        canEditOwn={editMode}
        canEditOther={editMode}
        tableView
        saveOnEnterPress
        disableEscapeKeyDown
        currentStaff={currentStaff}
        placeholder={formatMessage({ id: 'attendance-AddInternalNote' })}
        renderIcon={(disabled) => (
          <IconButton className="icon-Comments" disabled={disabled} sx={{ color: 'inherit' }}>
            {hasComment ? <NoteFilledIcon /> : <NotePlusIcon />}
          </IconButton>
        )}
      />
    </Box>
  );
};
